import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const FIELD_COLORS = {
  pest365: '#2563eb',
  pest365Plus: '#8b5cf6',
  oneTime: '#f59e0b',
  initialServiceVisit: '#16a34a',
  mosquitoTickControl: '#ec4899',
};

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  oneTime: 'One-Time',
  initialServiceVisit: 'Initial Service Visit',
  mosquitoTickControl: 'Mosquito & Tick',
};

const THRESHOLD_COLORS = {
  low: '#16a34a',
  moderate: '#f59e0b',
  high: '#dc2626',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  toggleContainer: {
    display: 'flex',
    gap: '4px',
    marginBottom: '12px',
    background: '#f3f4f6',
    borderRadius: '6px',
    padding: '3px',
    width: 'fit-content',
  },
  toggleButton: {
    padding: '5px 14px',
    borderRadius: '4px',
    border: 'none',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    transition: 'background 0.15s, color 0.15s',
  },
  toggleActive: {
    background: '#fff',
    color: '#1f2937',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  toggleInactive: {
    background: 'transparent',
    color: '#6b7280',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 3,
    flexShrink: 0,
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  contributorsSection: {
    marginTop: '16px',
    borderTop: '1px solid #f3f4f6',
    paddingTop: '16px',
  },
  contributorsTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '8px',
  },
  contributorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
    fontSize: '12px',
  },
  contributorLabel: {
    width: '28px',
    fontWeight: 600,
    color: '#374151',
  },
  contributorBarContainer: {
    flex: 1,
    height: '16px',
    background: '#f3f4f6',
    borderRadius: '3px',
    overflow: 'hidden',
    position: 'relative',
  },
  contributorBar: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  contributorValue: {
    width: '48px',
    textAlign: 'right',
    color: '#6b7280',
    fontSize: '11px',
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function getConcentrationLabel(value, thresholds) {
  if (value < thresholds.low) return { label: 'Unconcentrated', color: THRESHOLD_COLORS.low };
  if (value < thresholds.moderate) return { label: 'Moderate', color: THRESHOLD_COLORS.moderate };
  return { label: 'Highly Concentrated', color: THRESHOLD_COLORS.high };
}

export default function HerfindahlIndexChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [viewMode, setViewMode] = useState('overall');

  const hasData = useMemo(() => {
    return (
      data &&
      data.dates &&
      data.dates.length > 0 &&
      data.hhi &&
      data.hhi.length > 0 &&
      data.thresholds
    );
  }, [data]);

  const fieldKeys = useMemo(() => {
    if (!data || !data.byField) return [];
    return Object.keys(data.byField).filter((k) => FIELD_COLORS[k]);
  }, [data]);

  useEffect(() => {
    if (!hasData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const margin = { top: 20, right: 20, bottom: 50, left: 60 };
    const innerW = width - margin.left - margin.right;
    const innerH = 320 - margin.top - margin.bottom;

    svg.attr('width', width).attr('height', 320);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const parsedDates = data.dates.map((d) => new Date(d));
    const n = data.dates.length;

    // Determine Y domain based on all visible values
    let allValues = [...data.hhi];
    if (viewMode === 'byField' && data.byField) {
      fieldKeys.forEach((k) => {
        if (data.byField[k]) {
          allValues = allValues.concat(data.byField[k]);
        }
      });
    }
    // Always include the moderate threshold line at minimum
    allValues.push(data.thresholds.moderate + 200);
    const yMax = Math.max(d3.max(allValues) || 3000, data.thresholds.moderate + 500);
    const yPadding = yMax * 0.05;

    const xScale = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerW]);

    const yScale = d3.scaleLinear()
      .domain([0, yMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // Threshold background bands
    const lowThreshold = data.thresholds.low;
    const moderateThreshold = data.thresholds.moderate;
    const yDomainMax = yScale.domain()[1];

    // Green band: 0 to low threshold
    g.append('rect')
      .attr('x', 0)
      .attr('y', yScale(lowThreshold))
      .attr('width', innerW)
      .attr('height', yScale(0) - yScale(lowThreshold))
      .attr('fill', THRESHOLD_COLORS.low)
      .attr('fill-opacity', 0.05);

    // Yellow band: low to moderate threshold
    g.append('rect')
      .attr('x', 0)
      .attr('y', yScale(moderateThreshold))
      .attr('width', innerW)
      .attr('height', yScale(lowThreshold) - yScale(moderateThreshold))
      .attr('fill', THRESHOLD_COLORS.moderate)
      .attr('fill-opacity', 0.05);

    // Red band: moderate threshold to top
    g.append('rect')
      .attr('x', 0)
      .attr('y', yScale(yDomainMax))
      .attr('width', innerW)
      .attr('height', yScale(moderateThreshold) - yScale(yDomainMax))
      .attr('fill', THRESHOLD_COLORS.high)
      .attr('fill-opacity', 0.05);

    // Horizontal grid lines
    const yTicks = yScale.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Threshold lines with labels
    // Low threshold line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', yScale(lowThreshold))
      .attr('y2', yScale(lowThreshold))
      .attr('stroke', THRESHOLD_COLORS.low)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4')
      .attr('opacity', 0.7);

    g.append('text')
      .attr('x', innerW - 4)
      .attr('y', yScale(lowThreshold) - 6)
      .attr('text-anchor', 'end')
      .attr('fill', THRESHOLD_COLORS.low)
      .attr('font-size', '9px')
      .attr('font-weight', 600)
      .text(`Unconcentrated (${lowThreshold})`);

    // Moderate threshold line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', yScale(moderateThreshold))
      .attr('y2', yScale(moderateThreshold))
      .attr('stroke', THRESHOLD_COLORS.high)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4')
      .attr('opacity', 0.7);

    g.append('text')
      .attr('x', innerW - 4)
      .attr('y', yScale(moderateThreshold) - 6)
      .attr('text-anchor', 'end')
      .attr('fill', THRESHOLD_COLORS.high)
      .attr('font-size', '9px')
      .attr('font-weight', 600)
      .text(`Highly Concentrated (${moderateThreshold})`);

    // Draw lines based on view mode
    if (viewMode === 'overall') {
      // Area fill under HHI line
      const area = d3.area()
        .x((d, i) => xScale(parsedDates[i]))
        .y0(innerH)
        .y1((d) => yScale(d))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(data.hhi)
        .attr('d', area)
        .attr('fill', '#2563eb')
        .attr('fill-opacity', 0.08);

      // HHI line
      const line = d3.line()
        .x((d, i) => xScale(parsedDates[i]))
        .y((d) => yScale(d))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(data.hhi)
        .attr('d', line)
        .attr('fill', 'none')
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 2.5);

      // Dots on data points
      data.hhi.forEach((val, i) => {
        const concentration = getConcentrationLabel(val, data.thresholds);
        g.append('circle')
          .attr('cx', xScale(parsedDates[i]))
          .attr('cy', yScale(val))
          .attr('r', 3.5)
          .attr('fill', concentration.color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1.5);
      });
    } else {
      // Per-field breakdown lines
      fieldKeys.forEach((fieldKey) => {
        const values = data.byField[fieldKey];
        if (!values || values.length === 0) return;

        const line = d3.line()
          .x((d, i) => xScale(parsedDates[i]))
          .y((d) => yScale(d))
          .defined((d) => d != null)
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(values)
          .attr('d', line)
          .attr('fill', 'none')
          .attr('stroke', FIELD_COLORS[fieldKey])
          .attr('stroke-width', 2)
          .attr('opacity', 0.9);
      });
    }

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(xScale)
          .ticks(Math.min(n, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(yScale)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => v.toLocaleString())
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .text('HHI Value');

    // Crosshair line
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Hover overlay
    const bisector = d3.bisector((d) => d).left;

    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const hoveredDate = xScale.invert(mx);
        const timestamps = parsedDates.map((d) => d.getTime());
        let idx = bisector(timestamps, hoveredDate.getTime());

        // Snap to nearest point
        if (idx > 0 && idx < n) {
          const d0 = timestamps[idx - 1];
          const d1 = timestamps[idx];
          idx = (hoveredDate.getTime() - d0) > (d1 - hoveredDate.getTime()) ? idx : idx - 1;
        }
        idx = Math.max(0, Math.min(n - 1, idx));

        const cx = xScale(parsedDates[idx]);
        crosshair.attr('x1', cx).attr('x2', cx).style('display', null);

        const hhiVal = data.hhi[idx];
        const concentration = getConcentrationLabel(hhiVal, data.thresholds);

        const fieldValues = {};
        if (viewMode === 'byField' && data.byField) {
          fieldKeys.forEach((k) => {
            fieldValues[k] = data.byField[k] ? data.byField[k][idx] : null;
          });
        }

        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 10,
          date: data.dates[idx],
          hhi: hhiVal,
          concentration,
          fieldValues,
        });
      })
      .on('mouseleave', () => {
        crosshair.style('display', 'none');
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, hasData, viewMode, fieldKeys]);

  if (!hasData) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Market Concentration (Herfindahl Index)</div>
          <div style={styles.subtitle}>HHI measures pricing concentration across states/regions</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No concentration data available
        </div>
      </div>
    );
  }

  const latestHhi = data.hhi[data.hhi.length - 1];
  const latestConcentration = getConcentrationLabel(latestHhi, data.thresholds);

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2px' }}>
          <div style={styles.title}>Market Concentration (Herfindahl Index)</div>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              borderRadius: '9999px',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.025em',
              background: latestConcentration.color === THRESHOLD_COLORS.low ? '#dcfce7'
                : latestConcentration.color === THRESHOLD_COLORS.moderate ? '#fef3c7'
                : '#fee2e2',
              color: latestConcentration.color === THRESHOLD_COLORS.low ? '#166534'
                : latestConcentration.color === THRESHOLD_COLORS.moderate ? '#92400e'
                : '#991b1b',
            }}
          >
            {latestConcentration.label}
          </span>
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
            (Current: {latestHhi.toLocaleString()})
          </span>
        </div>
        <div style={styles.subtitle}>
          HHI = sum of squared market shares (0-10,000). Higher values indicate more concentrated pricing.
        </div>
      </div>

      {/* View mode toggle */}
      {fieldKeys.length > 0 && (
        <div style={styles.toggleContainer}>
          <button
            style={{
              ...styles.toggleButton,
              ...(viewMode === 'overall' ? styles.toggleActive : styles.toggleInactive),
            }}
            onClick={() => setViewMode('overall')}
          >
            Overall HHI
          </button>
          <button
            style={{
              ...styles.toggleButton,
              ...(viewMode === 'byField' ? styles.toggleActive : styles.toggleInactive),
            }}
            onClick={() => setViewMode('byField')}
          >
            By Price Field
          </button>
        </div>
      )}

      {/* Legend */}
      <div style={styles.legend}>
        {viewMode === 'overall' ? (
          <>
            <div style={styles.legendItem}>
              <div style={{ width: 14, height: 3, background: '#2563eb', borderRadius: 1 }} />
              <span>HHI</span>
            </div>
            <div style={styles.legendItem}>
              <svg width="16" height="10">
                <line x1="0" y1="5" x2="16" y2="5" stroke={THRESHOLD_COLORS.low} strokeWidth="1.5" strokeDasharray="4,3" />
              </svg>
              <span>Unconcentrated ({data.thresholds.low})</span>
            </div>
            <div style={styles.legendItem}>
              <svg width="16" height="10">
                <line x1="0" y1="5" x2="16" y2="5" stroke={THRESHOLD_COLORS.high} strokeWidth="1.5" strokeDasharray="4,3" />
              </svg>
              <span>Highly Concentrated ({data.thresholds.moderate})</span>
            </div>
          </>
        ) : (
          fieldKeys.map((k) => (
            <div key={k} style={styles.legendItem}>
              <span style={{ ...styles.legendSwatch, backgroundColor: FIELD_COLORS[k] }} />
              {FIELD_LABELS[k] || k}
            </div>
          ))
        )}
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Overall HHI</span>
            <span style={{ fontWeight: 600, color: tooltip.concentration.color }}>
              {tooltip.hhi != null ? tooltip.hhi.toLocaleString() : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '4px' }}>
            <span style={{ color: '#6b7280' }}>Classification</span>
            <span style={{ fontWeight: 600, color: tooltip.concentration.color }}>
              {tooltip.concentration.label}
            </span>
          </div>
          {viewMode === 'byField' && Object.keys(tooltip.fieldValues).length > 0 && (
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '4px' }}>
              {fieldKeys.map((k) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                  <span style={{ color: '#6b7280' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        marginRight: 6,
                        verticalAlign: 'middle',
                        backgroundColor: FIELD_COLORS[k],
                      }}
                    />
                    {FIELD_LABELS[k] || k}
                  </span>
                  <span style={{ fontWeight: 600 }}>
                    {tooltip.fieldValues[k] != null ? tooltip.fieldValues[k].toLocaleString() : '--'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Top Contributing States */}
      {data.topContributors && data.topContributors.length > 0 && (
        <div style={styles.contributorsSection}>
          <div style={styles.contributorsTitle}>Top Contributing States</div>
          {data.topContributors.slice(0, 8).map((contributor) => {
            const pct = (contributor.share * 100).toFixed(1);
            return (
              <div key={contributor.state} style={styles.contributorRow}>
                <span style={styles.contributorLabel}>{contributor.state}</span>
                <div style={styles.contributorBarContainer}>
                  <div
                    style={{
                      ...styles.contributorBar,
                      width: `${Math.min(contributor.share * 100 * 3, 100)}%`,
                      background: `linear-gradient(90deg, #2563eb, ${contributor.share > 0.15 ? '#8b5cf6' : '#60a5fa'})`,
                    }}
                  />
                </div>
                <span style={styles.contributorValue}>{pct}%</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
